import axios from 'axios'
import axiosInstance from '../axios'

const getAuthHeaders = () => {
  const authToken = localStorage.getItem('access-Token-Edify')
  return { headers: { Authorization: `Bearer ${authToken}` } }
}

const getAuthToken = () => localStorage.getItem('access-Token-Edify')

export type DraftProjectPayload = {
  name?: string
  description?: string
  zone?: string
  startDate?: string
  endDate?: string
  active?: boolean
}

export type ProjectAutofillSkillMatchKind = 'unresolved' | 'fallback_otros' | (string & Record<never, never>)

export type BudgetSheetSource = 'suggested' | 'user_edited' | 'reset'

export type BudgetSheetColumn = {
  id: string
  displayLabel?: string
}

export type BudgetSheetCellValue = string | number | null

export type BudgetSheetGridRow = {
  rowId: string
  cells: Record<string, BudgetSheetCellValue>
}

export type BudgetSheetBinding = {
  tempId: string
  selectedSkillId?: number
  rubroLabel: string
}

export type BudgetSheetSuggestedSnapshot = {
  columns: BudgetSheetColumn[]
  rows: BudgetSheetGridRow[]
}

export type BudgetSheetState = {
  templateVersion: string
  binding: BudgetSheetBinding
  columns: BudgetSheetColumn[]
  rows: BudgetSheetGridRow[]
  suggestedSnapshot?: BudgetSheetSuggestedSnapshot
  source?: BudgetSheetSource
  updatedAt?: string
}

export type DraftSubprojectPayload = {
  tempId?: string
  detectedLabel?: string
  selectedSkillId?: number
  description?: string
  status?: 'OPEN' | 'CLOSE'
  skillMatchKind?: ProjectAutofillSkillMatchKind
  matchedSkillName?: string
  matchScore?: number
  name?: string
  link?: string
  startDate?: string
  endDate?: string
  datesInheritedFromProject?: boolean
  sourcePages?: number[]
  documentationSlots?: {
    budget?: number[]
    metricComputation?: number[]
    technicalSpecifications?: number[]
    planos?: number[]
  }
  documentationFileIds?: {
    budget?: string
    metricComputation?: string
    technicalSpecifications?: string
    planos?: string
  }
  budgetSheetState?: BudgetSheetState
}

export type DraftPatchPayload = {
  project?: DraftProjectPayload
  subprojects?: DraftSubprojectPayload[]
  metadata?: Record<string, any>
}

export const createProjectAutofillDraft = async (payload: DraftPatchPayload) => {
  const response = await axiosInstance.post('/project-autofill/draft', payload, getAuthHeaders())
  return response.data
}

export const getProjectAutofillDraft = async (draftId: string) => {
  const response = await axiosInstance.get(`/project-autofill/draft/${draftId}`, getAuthHeaders())
  return response.data
}

export const patchProjectAutofillDraft = async (draftId: string, payload: DraftPatchPayload) => {
  const response = await axiosInstance.patch(`/project-autofill/draft/${draftId}`, payload, getAuthHeaders())
  return response.data
}

export type PublishProjectAutofillDraftResult = {
  message?: string
  proyectoId?: string
  subproyectoIds?: string[]
  draftId?: string
}

export function parsePublishProjectAutofillDraftResponse(res: unknown): PublishProjectAutofillDraftResult {
  const out: PublishProjectAutofillDraftResult = {}
  if (!res || typeof res !== 'object') return out
  const r = res as Record<string, unknown>
  if (typeof r.message === 'string') out.message = r.message
  const inner = r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  const pid = inner.proyectoId
  if (pid != null && String(pid).trim() !== '') out.proyectoId = String(pid).trim()
  const sids = inner.subproyectoIds
  if (Array.isArray(sids)) {
    out.subproyectoIds = sids.map((x) => String(x)).filter((s) => s.length > 0)
  }
  const did = inner.draftId
  if (did != null && String(did).trim() !== '') out.draftId = String(did).trim()
  return out
}

export function formatPublishProjectAutofillDraftError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data
    if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>
      const msg = o.message ?? o.error ?? o.mensaje
      if (typeof msg === 'string' && msg.trim()) return msg.trim()
      const blocking = o.blockingIssues
      if (Array.isArray(blocking) && blocking.length > 0) {
        const b = blocking.map((x) => String(x || '').trim()).filter(Boolean).join('. ')
        if (b) return b
      }
      const missing = o.missingForProjectAssign
      if (Array.isArray(missing) && missing.length > 0) {
        const m = missing.map((x) => String(x || '').trim()).filter(Boolean).join(', ')
        if (m) return m
      }
      const subIss = o.subprojectIssues
      if (Array.isArray(subIss) && subIss.length > 0) {
        const s = subIss
          .map((x) => {
            if (x && typeof x === 'object') {
              const r = x as Record<string, unknown>
              const parts = [r.message, r.tempId, r.skillId].filter((p) => p != null && String(p).trim() !== '')
              return parts.map(String).join(' — ')
            }
            return String(x || '').trim()
          })
          .filter(Boolean)
          .join('. ')
        if (s) return s
      }
    }
    return err.message || 'No se pudo publicar'
  }
  return 'No se pudo publicar'
}

export const publishProjectAutofillDraft = async (draftId: string): Promise<unknown> => {
  const response = await axiosInstance.post(
    `/project-autofill/draft/${draftId}/publish`,
    null,
    getAuthHeaders()
  )
  return response.data
}

export const postProjectAutofillDraftFile = async (
  draftId: string,
  file: File,
  tag?: string
) => {
  const token = getAuthToken()
  const fd = new FormData()
  fd.append('file', file)
  if (tag != null && String(tag).trim() !== '') {
    fd.append('tag', String(tag).trim())
  }
  const response = await axiosInstance.post(`/project-autofill/draft/${draftId}/files`, fd, {
    headers: { Authorization: `Bearer ${token}` }
  })
  return response.data
}

const DOCUMENTATION_PIPELINE_POLL_MAX_MS = 600000
const DOCUMENTATION_PIPELINE_POLL_INTERVAL_MS = 2000

export type DocumentInsightKind =
  | 'project_technical'
  | 'materials_list'
  | 'invoice_or_admin'
  | 'unreadable_or_empty'
  | 'other'
  | 'unclear'

export type DocumentInsightSuggestedActionId =
  | 'upload_technical_doc'
  | 'continue_anyway'
  | 'contact_support'

export type DocumentInsightSuggestedAction = {
  id: string
  label: string
}

export type DocumentInsight = {
  kind: DocumentInsightKind
  confidence: number
  titleForUser: string
  bodyForUser: string | null
  suggestedActions: DocumentInsightSuggestedAction[]
}

const DOCUMENT_INSIGHT_KINDS = new Set<string>([
  'project_technical',
  'materials_list',
  'invoice_or_admin',
  'unreadable_or_empty',
  'other',
  'unclear'
])

export function parseDocumentInsight(raw: unknown): DocumentInsight | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }
  const o = raw as Record<string, unknown>
  const kindRaw = typeof o.kind === 'string' ? o.kind.trim() : ''
  if (!DOCUMENT_INSIGHT_KINDS.has(kindRaw)) {
    return null
  }
  const kind = kindRaw as DocumentInsightKind
  const c = o.confidence
  const confidence =
    typeof c === 'number' && Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0
  const titleRaw = typeof o.titleForUser === 'string' ? o.titleForUser.trim() : ''
  if (!titleRaw) {
    return null
  }
  let bodyForUser: string | null = null
  if (o.bodyForUser != null) {
    if (typeof o.bodyForUser === 'string') {
      const b = o.bodyForUser.trim()
      bodyForUser = b.length > 0 ? b : null
    }
  }
  const suggestedActions: DocumentInsightSuggestedAction[] = []
  const arr = o.suggestedActions
  if (Array.isArray(arr)) {
    for (const item of arr) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue
      const a = item as Record<string, unknown>
      const id = typeof a.id === 'string' && a.id.trim() ? a.id.trim() : ''
      const label = typeof a.label === 'string' && a.label.trim() ? a.label.trim() : ''
      if (id && label) {
        suggestedActions.push({ id, label })
      }
    }
  }
  return { kind, confidence, titleForUser: titleRaw, bodyForUser, suggestedActions }
}

export type DocumentationPipelineStageProgress = {
  done: number
  total: number
}

export type DocumentationPipelineStatus = {
  phase: string
  detailPhase?: string
  stage?: string
  stageProgress?: DocumentationPipelineStageProgress
  stageUpdatedAt?: string | null
  errorMessage?: string
  draftId?: string | null
  startedAt?: string | null
  finishedAt?: string | null
  updatedAt?: string | null
  jobId?: string | null
  primaryFileId?: string | null
  primaryFileOriginalName?: string | null
  primaryFileMimeType?: string | null
  primaryFileSizeBytes?: number | null
  documentInsight?: DocumentInsight | null
}

export type DocumentationPipelinePollUpdate = {
  loaderPhase: string
  status: DocumentationPipelineStatus
}

export function formatDocumentationPipelineFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return ''
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`
  }
  const kb = bytes / 1024
  if (kb < 1024) {
    return `${Math.round(kb)} KB`
  }
  const mb = bytes / (1024 * 1024)
  const label = mb >= 10 ? String(Math.round(mb)) : String(Math.round(mb * 10) / 10)
  return `${label} MB`
}

function readOptionalTrimmedString(v: unknown): string | null {
  if (v == null) {
    return null
  }
  if (typeof v !== 'string') {
    return null
  }
  const t = v.trim()
  return t.length > 0 ? t : null
}

function readOptionalIsoString(v: unknown): string | null {
  if (v == null) {
    return null
  }
  if (typeof v !== 'string') {
    return null
  }
  const t = v.trim()
  return t.length > 0 ? t : null
}

function readOptionalNumber(v: unknown): number | null {
  if (v == null) {
    return null
  }
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    return null
  }
  return v
}

function parseStageProgress(raw: unknown): DocumentationPipelineStageProgress | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  const o = raw as Record<string, unknown>
  const doneRaw = o.done
  const totalRaw = o.total
  const done =
    typeof doneRaw === 'number' && Number.isFinite(doneRaw) ? Math.max(0, Math.floor(doneRaw)) : null
  const total =
    typeof totalRaw === 'number' && Number.isFinite(totalRaw) ? Math.max(0, Math.floor(totalRaw)) : null
  if (done === null || total === null) {
    return undefined
  }
  return { done, total }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export function parseDocumentationPipelinePayload(raw: unknown): DocumentationPipelineStatus {
  if (!raw || typeof raw !== 'object') {
    return { phase: 'processing' }
  }
  const r = raw as Record<string, unknown>
  const inner =
    r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  const phase = typeof inner.phase === 'string' && inner.phase.length > 0 ? inner.phase : 'processing'
  const detailPhaseRaw = inner.detailPhase
  const detailPhase =
    typeof detailPhaseRaw === 'string' && detailPhaseRaw.trim().length > 0
      ? detailPhaseRaw.trim()
      : undefined
  const stageRaw = inner.stage
  const stage =
    typeof stageRaw === 'string' && stageRaw.trim().length > 0 ? stageRaw.trim() : undefined
  const stageProgress = parseStageProgress(inner.stageProgress)
  const stageUpdatedAt = readOptionalIsoString(inner.stageUpdatedAt)
  const errorMessage =
    typeof inner.errorMessage === 'string' && inner.errorMessage.trim().length > 0
      ? inner.errorMessage.trim()
      : undefined
  const draftId = readOptionalTrimmedString(inner.draftId)
  const startedAt = readOptionalIsoString(inner.startedAt)
  const finishedAt = readOptionalIsoString(inner.finishedAt)
  const updatedAt = readOptionalIsoString(inner.updatedAt)
  const jobId = readOptionalTrimmedString(inner.jobId)
  const primaryFileId = readOptionalTrimmedString(inner.primaryFileId)
  const primaryFileOriginalName = readOptionalTrimmedString(inner.primaryFileOriginalName)
  const primaryFileMimeType = readOptionalTrimmedString(inner.primaryFileMimeType)
  const primaryFileSizeBytes = readOptionalNumber(inner.primaryFileSizeBytes)
  let documentInsight: DocumentInsight | null | undefined
  if (!Object.prototype.hasOwnProperty.call(inner, 'documentInsight')) {
    documentInsight = undefined
  } else if (inner.documentInsight === null) {
    documentInsight = null
  } else {
    documentInsight = parseDocumentInsight(inner.documentInsight)
  }
  return {
    phase,
    detailPhase,
    stage,
    stageProgress,
    stageUpdatedAt,
    errorMessage,
    draftId,
    startedAt,
    finishedAt,
    updatedAt,
    jobId,
    primaryFileId,
    primaryFileOriginalName,
    primaryFileMimeType,
    primaryFileSizeBytes,
    documentInsight
  }
}

export function documentationPipelinePhaseForLoader(status: DocumentationPipelineStatus): string {
  const fromStage =
    typeof status.stage === 'string' && status.stage.trim().length > 0 ? status.stage.trim() : ''
  const fromDetail =
    typeof status.detailPhase === 'string' && status.detailPhase.trim().length > 0
      ? status.detailPhase.trim()
      : ''
  const sub = fromStage || fromDetail
  if (
    (status.phase === 'processing' ||
      status.phase === 'awaiting_analysis' ||
      status.phase === 'no_document') &&
    sub.length > 0
  ) {
    return sub
  }
  return status.phase
}

export async function getProjectAutofillDocumentationPipeline(
  draftId: string
): Promise<DocumentationPipelineStatus> {
  const response = await axiosInstance.get(
    `/project-autofill/draft/${draftId}/documentation-pipeline`,
    getAuthHeaders()
  )
  return parseDocumentationPipelinePayload(response.data)
}

function pipelineError(
  code: string,
  message: string,
  userMessage?: string,
  documentInsight?: DocumentInsight | null
): Error & { code?: string; userMessage?: string; documentInsight?: DocumentInsight | null } {
  const e = new Error(message) as Error & {
    code?: string
    userMessage?: string
    documentInsight?: DocumentInsight | null
  }
  e.code = code
  e.userMessage = userMessage ?? message
  if (documentInsight !== undefined) {
    e.documentInsight = documentInsight
  }
  return e
}

export async function waitForProjectAutofillDocumentationPipeline(
  draftId: string,
  onUpdate: (update: DocumentationPipelinePollUpdate) => void,
  options?: { maxWaitMs?: number; intervalMs?: number; signal?: AbortSignal }
): Promise<DocumentationPipelineStatus> {
  const maxWaitMs = options?.maxWaitMs ?? DOCUMENTATION_PIPELINE_POLL_MAX_MS
  const intervalMs = options?.intervalMs ?? DOCUMENTATION_PIPELINE_POLL_INTERVAL_MS
  const deadline = Date.now() + maxWaitMs
  const signal = options?.signal

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string }
      err.name = 'AbortError'
      throw err
    }
    const status = await getProjectAutofillDocumentationPipeline(draftId)
    const { phase, errorMessage } = status
    onUpdate({
      loaderPhase: documentationPipelinePhaseForLoader(status),
      status
    })
    if (phase === 'ready') {
      return status
    }
    if (phase === 'failed') {
      const msg =
        errorMessage && errorMessage.length > 0
          ? errorMessage
          : 'El análisis del documento falló.'
      throw pipelineError(
        'DOCUMENTATION_PIPELINE_FAILED',
        msg,
        msg,
        status.documentInsight ?? null
      )
    }
    await delay(intervalMs)
  }
  throw pipelineError(
    'STREAM_TIMEOUT',
    'STREAM_TIMEOUT',
    'El análisis del documento tardó demasiado. Intentá de nuevo en unos minutos.'
  )
}

export type PostProjectAutofillDraftFromDocumentResult =
  | { mode: 'async'; draftId: string; body: unknown }
  | { mode: 'sync'; body: unknown }

export function extractProjectAutofillDraftIdFromResponse(raw: unknown): string {
  const parsed = parsePublishProjectAutofillDraftResponse(raw)
  let draftId = parsed.draftId ?? ''
  if (raw && typeof raw === 'object') {
    const d = raw as Record<string, unknown>
    const inner =
      d.data && typeof d.data === 'object' ? (d.data as Record<string, unknown>) : d
    const fromDraftId = inner.draftId
    if (fromDraftId != null && String(fromDraftId).trim() !== '') {
      draftId = String(fromDraftId).trim()
    } else {
      const fromId = inner.id
      if (fromId != null && String(fromId).trim() !== '') {
        draftId = String(fromId).trim()
      }
    }
  }
  return draftId
}

export async function postProjectAutofillDraftFromDocument(
  file: File
): Promise<PostProjectAutofillDraftFromDocumentResult> {
  const fd = new FormData()
  fd.append('file', file)
  const response = await axiosInstance.post('/project-autofill/draft/from-document', fd, {
    ...getAuthHeaders()
  })
  if (response.status === 202) {
    const raw = response.data
    const draftId = extractProjectAutofillDraftIdFromResponse(raw)
    return { mode: 'async', draftId, body: raw }
  }
  return { mode: 'sync', body: response.data }
}

export type PostProjectAutofillDocumentationAnalyzeAsyncResult = {
  draftId?: string
  enqueued?: boolean
  skippedReason?: string | null
  raw: unknown
}

export function parseDocumentationAnalyzeAsyncResponse(
  raw: unknown
): PostProjectAutofillDocumentationAnalyzeAsyncResult {
  const out: PostProjectAutofillDocumentationAnalyzeAsyncResult = { raw }
  if (!raw || typeof raw !== 'object') return out
  const r = raw as Record<string, unknown>
  const inner =
    r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  if (inner.draftId != null && String(inner.draftId).trim() !== '') {
    out.draftId = String(inner.draftId).trim()
  }
  if (typeof inner.enqueued === 'boolean') {
    out.enqueued = inner.enqueued
  }
  if (inner.skippedReason === null) {
    out.skippedReason = null
  } else if (typeof inner.skippedReason === 'string') {
    out.skippedReason = inner.skippedReason
  }
  return out
}

export async function postProjectAutofillDocumentationAnalyzeAsync(
  draftId: string
): Promise<PostProjectAutofillDocumentationAnalyzeAsyncResult> {
  const response = await axiosInstance.post(
    `/project-autofill/draft/${draftId}/documentation-analyze-async`,
    null,
    getAuthHeaders()
  )
  return parseDocumentationAnalyzeAsyncResponse(response.data)
}
